/**
 * One piece of evidence (layer 3): a one-line row (the "Asserted that ..." text,
 * the item's own status, and for perf the shas and verdict) that expands to the
 * kind's full evidence.
 *
 * @module features/proofs/components/EvidenceItem
 */
import React from 'react';
import { Badge, CollapsibleSection, Text } from '@chatsdet/ui';
import type { ProofEvidence } from '../types';
import { E2eEvidenceBody } from './E2eEvidenceBody';
import { ManualBadge } from './ManualBadge';
import { ManualEvidenceBody, manualFirstLine } from './ManualEvidenceBody';
import { ItemStatusBadge } from './ItemStatusBadge';
import { PerfEvidenceBody, perfRowSummary } from './PerfEvidenceBody';

export const EvidenceItem: React.FC<{ evidence: ProofEvidence; defaultExpanded: boolean }> = ({
  evidence,
  defaultExpanded,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const label = evidence.kind === 'manual' ? manualFirstLine(evidence) : evidence.asserted;
  const body = (() => {
    switch (evidence.kind) {
      case 'e2e':
        return <E2eEvidenceBody item={evidence} expanded={expanded} />;
      case 'manual':
        return <ManualEvidenceBody item={evidence} />;
      case 'perf':
        return <PerfEvidenceBody item={evidence} />;
    }
  })();
  return (
    <CollapsibleSection
      hideBorder
      label={label}
      header={
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Text>{label}</Text>
          {evidence.kind === 'manual' ? (
            <ManualBadge status={evidence.status} testId="manual-item-badge" />
          ) : (
            <ItemStatusBadge status={evidence.status} />
          )}
          {evidence.kind === 'e2e' && evidence.existing && (
            <Badge
              variant="secondary"
              size="sm"
              data-testid="existing-test-badge"
              title="Existing approved test: proves it still works; never promoted"
            >
              Existing test
            </Badge>
          )}
          {evidence.kind === 'perf' && <Text color="muted" size="sm">{perfRowSummary(evidence)}</Text>}
        </div>
      }
      isExpanded={expanded}
      onExpandedChange={setExpanded}
      bodyClassName="space-y-4 pl-6"
    >
      {body}
    </CollapsibleSection>
  );
};
